import { useState, type MouseEvent } from 'react';
import {
  Button,
  Badge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  StepProgressBar,
  StepProgressItem,
  Popover,
  PopoverTrigger,
  IconLucide,
  PopoverContent
} from '@yakit/ui-components';

import { useVDLModalStore } from '@VDL/store/VDLModalStore';
import { useFetchCancel } from '@VDL/hooks/useFetchCancel';

import type { IVDLAgreement } from '@VDL/api/types/IVDLAgreement';

interface VDLAgreementButtonProps {
  agreementObject: IVDLAgreement | undefined;
  level0Code: string | undefined;
}

export const VDLAgreementButton = ({
  agreementObject,
  level0Code
}: VDLAgreementButtonProps) => {
  const { setDefaultIframe: setModalIframe, setPassportModal } =
    useVDLModalStore();
  const { fetchCancel, isLoading: isLoadingCancel } = useFetchCancel();
  const [showPopover, setShowPopover] = useState(false);

  const hanldeClickAgreementButton = async (
    agreementObject: IVDLAgreement,
    level0Code: string
  ) => {
    switch (agreementObject.tip) {
      case 'utver':
        setModalIframe({
          type: 'default',
          title: 'Утверждение',
          src: `/v8/passport/${level0Code}?vdl`
        });
        break;
      case 'preview':
        setPassportModal({
          code: level0Code
        });
        break;
    }
  };

  const handleCancelAgreement = async (e: MouseEvent, level0Code: string) => {
    e.stopPropagation();

    fetchCancel(level0Code);
  };

  if (!agreementObject || !level0Code) return null;

  if (agreementObject.show_status_name) {
    return (
      <Button
        size="xs"
        variant="secondary-glass"
        className="cursor-pointer"
        onClick={() => hanldeClickAgreementButton(agreementObject, level0Code)}
      >
        {agreementObject.status_name}
      </Button>
    );
  } else {
    return (
      <StepProgressBar
        shape="round"
        className="cursor-pointer h-7"
        onClick={() => {
          setModalIframe({
            type: 'default',
            title: 'Утверждение',
            src: `/v8/passport/${level0Code}?vdl`
          });
        }}
      >
        {agreementObject.sogl_statuses.map((statusObject, statusIndex) => (
          <StepProgressItem
            key={statusIndex}
            className="cursor-auto"
            stepsLength={agreementObject.sogl_statuses.length}
            index={statusIndex}
            variant="glass"
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="h-2 w-2 rounded-full transition-all hover:opacity-50 hover:shadow-sm"
                    style={{
                      backgroundColor: statusObject.color
                    }}
                  ></div>
                </TooltipTrigger>
                <TooltipContent
                  className="cursor-auto"
                  variant="default"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex flex-col gap-2 py-2">
                    <div className="font-semibold">{statusObject.name}</div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Статус:</span>
                      <Badge
                        style={{
                          backgroundColor: statusObject.color
                        }}
                      >
                        {statusObject.status_name}
                      </Badge>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </StepProgressItem>
        ))}
        <Popover open={showPopover} onOpenChange={setShowPopover}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              className="group ml-4 h-3 w-3 cursor-pointer"
              size="icon"
              title="Отменить все этапы согласования"
              onClick={(e) => e.stopPropagation()}
            >
              <IconLucide
                name="CircleX"
                className="fill-muted-foreground/5 group-hover:stroke-destructive/80 stroke-muted-foreground h-full w-full"
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto"
            align="start"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <IconLucide
                name="CircleAlert"
                className="mt-0.5 h-4 w-4 shrink-0"
              />
              <span className="whitespace-nowrap">
                Отменить все этапы согласования?
              </span>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                size="xs"
                variant="outline"
                className="cursor-pointer"
                autoFocus
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPopover(false);
                }}
              >
                Нет
              </Button>
              <Button
                size="xs"
                variant="outline"
                className="bg-destructive/80 text-accent hover:bg-destructive/70 hover:text-accent cursor-pointer"
                disabled={isLoadingCancel}
                onClick={(e) => handleCancelAgreement(e, level0Code)}
              >
                {isLoadingCancel ? (
                  <>
                    В процессе{' '}
                    <IconLucide
                      name="LoaderCircle"
                      className="ml-2 h-4 w-4 animate-spin"
                    />
                  </>
                ) : (
                  'Да'
                )}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </StepProgressBar>
    );
  }
};
